// Tickets: triage queue and the workflow of one ticket (doc 26.4).
import { api } from "../lib/api";
import { PRIORITIES, SERVICE_CATEGORIES } from "../lib/catalog";
import { fmt, t } from "../lib/i18n";
import { Link, navigate } from "../lib/router";
import { h, invalidate, local, resource } from "../lib/ui";
import {
  act, Async, Button, Chip, confirmDialog, Dialog, Field, formData, isBusy, LoadError, Loading, openDialog,
  Prio, Select, TicketStatus, toCents, Toggle,
} from "../shared/components";
import { PageHead } from "./app";

export function newTicketDialog(homeId: string | null, onCreated: (id: string) => void) {
  const homes = homeId ? null : resource("admin.home.list..", () => api.get("/api/admin/homes"));
  openDialog((close) => (
    <Dialog title={t("admin.tickets.newTitle")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        act("ticket.create", async () => {
          const res = await api.post("/api/admin/tickets", {
            home_id: homeId ?? d.home_id, service: d.service, title: d.title, description: d.description, priority: d.priority, source: d.source,
          });
          close();
          invalidate("admin.tickets");
          onCreated(res.id);
        }, t("admin.tickets.created"));
      }}>
        {!homeId ? (
          <Field label={t("fields.home")} id="t-home">
            <Select id="t-home" name="home_id" options={(homes?.data ?? []).map((x: any) => ({ value: x.id, label: `${x.code} · ${x.address_line1}` }))} required />
          </Field>
        ) : null}
        <Field label={t("fields.title")} id="t-title"><input class="input" id="t-title" name="title" required maxLength={160} /></Field>
        <div class="cols-2">
          <Field label={t("fields.service")} id="t-svc"><Select id="t-svc" name="service" value="plumbing" options={SERVICE_CATEGORIES.map((x) => ({ value: x, label: t(`service.${x}`) }))} /></Field>
          <Field label={t("fields.priority")} id="t-pr"><Select id="t-pr" name="priority" value="P3" options={PRIORITIES.map((x) => ({ value: x, label: `${x} · ${t(`priority.${x}`)}` }))} /></Field>
        </div>
        <Field label={t("fields.source")} id="t-src">
          <Select id="t-src" name="source" value="request" options={["request", "alert", "visit_finding"].map((x) => ({ value: x, label: t(`ticketSource.${x}`) }))} />
        </Field>
        <Field label={t("fields.description")} id="t-desc"><textarea class="textarea" id="t-desc" name="description" maxLength={4000} /></Field>
        <div class="row end">
          <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
          <Button type="submit" busy={isBusy("ticket.create")}>{t("admin.tickets.create")}</Button>
        </div>
      </form>
    </Dialog>
  ));
}

export function TicketsPage() {
  const s = local("screen:tickets", () => ({ view: "open", priority: "" }));
  const r = resource(`admin.tickets.${s.view}.${s.priority}`, () => api.get(`/api/admin/tickets?view=${s.view}&priority=${s.priority}`));
  return (
    <div class="stack loose">
      <PageHead title={t("admin.tickets.title")} intro={t("admin.tickets.intro")}
        actions={<Button icon="plus" onClick={() => newTicketDialog(null, (id) => navigate(`/admin/tickets/${id}`))}>{t("admin.tickets.new")}</Button>} />
      <section class="panel">
        <div class="panel-head" style="flex-wrap:wrap">
          <div class="seg" role="group">
            {["open", "closed", "all"].map((v) => (
              <button aria-pressed={String(s.view === v)} onClick={() => s.set({ view: v })}>{t(`admin.tickets.views.${v}`)}</button>
            ))}
          </div>
          <div style="width:200px">
            <Select value={s.priority} onChange={(v) => s.set({ priority: v })} options={[{ value: "", label: t("admin.tickets.allPriorities") }, ...PRIORITIES.map((p) => ({ value: p, label: `${p} · ${t(`priority.${p}`)}` }))]} />
          </div>
        </div>
        {Async(r, (rows: any[]) => rows.length ? (
          <div class="table-wrap">
            <table class="table">
              <thead><tr><th></th><th>{t("fields.title")}</th><th>{t("fields.home")}</th><th>{t("fields.status")}</th><th class="right">{t("admin.tickets.estimate")}</th><th class="right">{t("admin.tickets.age")}</th></tr></thead>
              <tbody>
                {rows.map((x) => (
                  <tr class="link" onClick={() => navigate(`/admin/tickets/${x.id}`)}>
                    <td style="width:44px"><Prio p={x.priority} /></td>
                    <td><span class="strong">{x.title}</span><span class="sub">#{x.number} · {t(`service.${x.service}`)}{x.partner_name ? ` · ${x.partner_name}` : ""}</span></td>
                    <td><span>{x.address_line1}</span><span class="sub">{x.home_code}</span></td>
                    <td><TicketStatus status={x.status} /></td>
                    <td class="right tabular">{x.estimate_cents !== null ? fmt.money(x.estimate_cents) : "—"}</td>
                    <td class="right small muted nowrap">{fmt.relative(x.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div class="empty"><strong>{t("admin.tickets.emptyTitle")}</strong><span>{t("admin.tickets.emptyBody")}</span></div>, 6)}
      </section>
    </div>
  );
}

function estimateDialog(ticket: any, home: any, onDone: () => void) {
  openDialog((close) => (
    <Dialog title={t("admin.tickets.addEstimate")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        const cents = toCents(d.amount);
        if (cents === null) return;
        act("ticket.estimate", async () => {
          const res = await api.post(`/api/admin/tickets/${ticket.id}/estimates`, { amount_cents: cents, description: d.description });
          close();
          onDone();
          return res;
        }, t("admin.tickets.estimateSent"));
      }}>
        {ticket.priority === "P1" ? <div class="note warn small">{t("admin.tickets.emergencyNote", { cap: fmt.money(home.emergency_cap_cents) })}</div> : null}
        <Field label={t("admin.tickets.amount")} id="e-amt" hint={t("admin.tickets.amountHint")}>
          <div class="input-affix"><span class="affix">$</span><input class="input tabular" id="e-amt" name="amount" inputMode="decimal" required /></div>
        </Field>
        <Field label={t("admin.tickets.quoteText")} id="e-desc" hint={t("admin.tickets.quoteHint")}><textarea class="textarea" id="e-desc" name="description" required /></Field>
        <div class="row end">
          <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
          <Button type="submit" busy={isBusy("ticket.estimate")}>{t("admin.tickets.sendEstimate")}</Button>
        </div>
      </form>
    </Dialog>
  ));
}

export function TicketDetail(props: { id: string }) {
  const r = resource(`admin.ticket.${props.id}`, () => api.get(`/api/admin/tickets/${props.id}`));
  const partners = resource("admin.partners", () => api.get("/api/admin/partners"));
  if (r.error && !r.data) return <LoadError error={r.error} retry={() => r.reload()} />;
  if (!r.data) return <Loading rows={8} />;
  const { ticket, home, events, estimates, next } = r.data;
  const reload = () => { r.reload(); invalidate("admin.tickets"); };
  const patch = (body: any) => act("ticket.patch", async () => { await api.patch(`/api/admin/tickets/${ticket.id}`, body); reload(); });
  const move = async (to: string) => {
    if (["cancelled", "disputed"].includes(to) && !(await confirmDialog({ title: t(`admin.tickets.confirm.${to}.title`), body: t(`admin.tickets.confirm.${to}.body`), confirm: t(`ticketAction.${to}`), danger: true }))) return;
    act(`ticket.move.${to}`, async () => { await api.post(`/api/admin/tickets/${ticket.id}/status`, { to }); reload(); }, t("admin.tickets.moved", { status: t(`ticketStatus.${to}`) }));
  };
  const comment = (e: Event) => {
    const d = formData(e);
    const form = e.currentTarget as HTMLFormElement;
    act("ticket.comment", async () => { await api.post(`/api/admin/tickets/${ticket.id}/comment`, { note: d.note }); form.reset(); r.reload(); });
  };
  const partnerOpts = (partners.data ?? []).filter((p: any) => p.status === "active").map((p: any) => ({ value: p.id, label: p.name }));
  const canEstimate = ["triaged", "estimating", "awaiting_approval"].includes(ticket.status);
  const pending = estimates.find((e: any) => e.status === "pending");

  return (
    <div class="stack loose">
      <PageHead
        crumbs={[{ href: "/admin/tickets", label: t("admin.tickets.title") }]}
        title={ticket.title}
        intro={`#${ticket.number} · ${t(`service.${ticket.service}`)} · ${t(`ticketSource.${ticket.source}`)} · ${fmt.dateTime(ticket.created_at)}${ticket.created_by_name ? ` · ${ticket.created_by_name}` : ""}`}
        actions={[<Prio p={ticket.priority} />, <TicketStatus status={ticket.status} />]}
      />
      <div style="display:grid;grid-template-columns:minmax(0,1fr) 340px;gap:20px;align-items:start" class="ticket-layout">
        <div class="stack loose">
          {ticket.description ? <section class="panel panel-pad"><p style="white-space:pre-wrap">{ticket.description}</p></section> : null}

          <section class="panel">
            <div class="panel-head"><h2>{t("admin.tickets.nextStep")}</h2></div>
            <div class="panel-body stack">
              {ticket.status === "awaiting_approval" && pending ? (
                <div class="note">{t("admin.tickets.waitingCustomer", { amount: fmt.money(pending.amount_cents) })}</div>
              ) : null}
              {ticket.status === "awaiting_resident_approval" ? <div class="note">{t("admin.tickets.waitingResident")}</div> : null}
              {ticket.status === "triaged" && !ticket.route ? <div class="note warn small">{t("admin.tickets.chooseRoute")}</div> : null}
              <div class="row">
                {canEstimate ? <Button icon="plus" onClick={() => estimateDialog(ticket, home, reload)}>{t("admin.tickets.addEstimate")}</Button> : null}
                {next.filter((s: string) => !(canEstimate && s === "awaiting_approval")).map((s: string) => (
                  <Button kind={["cancelled", "disputed"].includes(s) ? "danger-outline" : "secondary"} busy={isBusy(`ticket.move.${s}`)} onClick={() => move(s)}>
                    {t(`ticketAction.${s}`)}
                  </Button>
                ))}
                {!next.length && !canEstimate ? <span class="muted small">{t("admin.tickets.noActions")}</span> : null}
              </div>
            </div>
          </section>

          <section class="panel">
            <div class="panel-head"><h2>{t("admin.tickets.history")}</h2></div>
            <div class="panel-body stack">
              <div class="timeline">
                {events.map((e: any) => (
                  <div class="ev">
                    <div class="stack tight" style="gap:2px">
                      <span class="small">
                        <span class="strong">{e.actor ?? t("admin.tickets.system")}</span>{" "}
                        {e.kind === "status" ? t("admin.tickets.ev.status", { to: t(`ticketStatus.${e.to_status}`) })
                          : e.kind === "created" ? t("admin.tickets.ev.created")
                          : e.kind === "estimate" ? t(e.note === "emergency_preapproved" ? "admin.tickets.ev.estimateEmergency" : "admin.tickets.ev.estimate")
                          : e.kind === "approval" ? t("admin.tickets.ev.approval")
                          : e.kind === "assignment" ? t("admin.tickets.ev.assignment")
                          : t("admin.tickets.ev.comment")}
                      </span>
                      {e.kind === "comment" && e.note ? <span class="small" style="white-space:pre-wrap">{e.note}</span> : null}
                      <span class="xs muted">{fmt.dateTime(e.at)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <form class="stack tight" onSubmit={comment}>
                <textarea class="textarea" name="note" required placeholder={t("admin.tickets.commentPlaceholder")} aria-label={t("admin.tickets.comment")} style="min-height:70px" />
                <div class="row end"><Button type="submit" kind="secondary" size="small" busy={isBusy("ticket.comment")}>{t("admin.tickets.addComment")}</Button></div>
              </form>
            </div>
          </section>
        </div>

        <div class="stack loose">
          <section class="panel">
            <div class="panel-head"><h2>{t("fields.home")}</h2></div>
            <div class="panel-body stack tight">
              <Link href={`/admin/homes/${home.id}`} class="strong">{home.nickname || home.address_line1}</Link>
              <span class="small muted">{home.code} · {home.city}</span>
              <span class="small">{t("admin.tickets.emergencyCap", { cap: fmt.money(home.emergency_cap_cents) })}</span>
              {home.access_revoked_at ? <Chip kind="critical">{t("admin.homes.accessRevoked")}</Chip> : null}
            </div>
          </section>
          <section class="panel">
            <div class="panel-head"><h2>{t("admin.tickets.dispatch")}</h2></div>
            <div class="panel-body stack">
              <Field label={t("fields.priority")} id="tp">
                <Select id="tp" value={ticket.priority} onChange={(v) => patch({ priority: v })} options={PRIORITIES.map((p) => ({ value: p, label: `${p} · ${t(`priority.${p}`)}` }))} />
              </Field>
              <Field label={t("fields.service")} id="ts">
                <Select id="ts" value={ticket.service} onChange={(v) => patch({ service: v })} options={SERVICE_CATEGORIES.map((x) => ({ value: x, label: t(`service.${x}`) }))} />
              </Field>
              <Field label={t("admin.tickets.route")} id="tr">
                <Select id="tr" value={ticket.route ?? ""} onChange={(v) => patch({ route: v || null })}
                  options={[{ value: "", label: t("admin.tickets.routeUnset") }, ...["partner", "trusted_provider", "staff"].map((x) => ({ value: x, label: t(`route.${x}`) }))]} />
              </Field>
              {ticket.route === "partner" ? (
                <Field label={t("admin.tickets.partner")} id="tpa">
                  <Select id="tpa" value={ticket.partner_id ?? ""} onChange={(v) => patch({ partner_id: v || null })} options={[{ value: "", label: t("common.none") }, ...partnerOpts]} />
                </Field>
              ) : null}
              {ticket.route === "partner" ? (
                <div class="row between">
                  <span class="small">{t("admin.tickets.accompaniment")}</span>
                  <Toggle checked={ticket.accompaniment} onChange={(v) => patch({ accompaniment: v })} />
                </div>
              ) : null}
            </div>
          </section>
          <section class="panel">
            <div class="panel-head"><h2>{t("admin.tickets.estimates")}</h2></div>
            {estimates.length ? (
              <table class="table"><tbody>
                {estimates.map((e: any) => (
                  <tr>
                    <td><span class="strong tabular">{fmt.money(e.amount_cents)}</span><span class="sub">{fmt.date(e.created_at)}</span></td>
                    <td class="right"><Chip kind={e.status === "approved" ? "good" : e.status === "pending" ? "accent" : "neutral"}>{t(`estimateStatus.${e.status}`)}</Chip>
                      {e.decided_by_name ? <span class="sub">{e.decided_by_name}</span> : null}</td>
                  </tr>
                ))}
              </tbody></table>
            ) : <div class="empty"><span>{t("admin.tickets.noEstimates")}</span></div>}
          </section>
        </div>
      </div>
    </div>
  );
}
